import { z } from 'zod';
import { ClientInfo, Id, Timestamp, singleLine } from './common';
import { endpoint } from './endpoint';
import { lenient } from './lenient';
import { LIMITS } from './limits';
import { LoginOk, NewPassword } from './auth';

/** Invitation lifecycle. Separate from mail delivery state. */
export const InvitationStatus = z.enum(['REQUESTED', 'PENDING', 'ACCEPTED', 'EXPIRED', 'REVOKED']);
export type InvitationStatus = z.infer<typeof InvitationStatus>;

/**
 * Mail delivery as reported by the cloud mail adapter. `ACCEPTED_BY_PROVIDER`
 * means the provider took the message — not that it reached the inbox.
 */
export const MailStatus = z.enum(['QUEUED', 'ACCEPTED_BY_PROVIDER', 'FAILED', 'BOUNCED']);
export type MailStatus = z.infer<typeof MailStatus>;

export const PersonRef = z.object({ id: Id, displayName: z.string() });

export const Invitation = z.object({
  id: Id,
  email: z.string(),
  status: lenient(InvitationStatus, 'EXPIRED'),
  mailStatus: lenient(MailStatus, 'QUEUED').nullable(),
  role: z.literal('member'),
  requestedBy: PersonRef.nullable(),
  invitedBy: PersonRef.nullable(),
  createdAt: Timestamp,
  sentAt: Timestamp.nullable(),
  expiresAt: Timestamp.nullable(),
  acceptedAt: Timestamp.nullable(),
  revokedAt: Timestamp.nullable(),
  /** Sanitised provider error code (never the link or token). */
  mailError: z.string().nullable(),
});
export type Invitation = z.infer<typeof Invitation>;

export const InviteOutcome = z.enum(['pending', 'requested', 'duplicate', 'already_member', 'invalid', 'rate_limited']);

/** What the accept page shows before anything is consumed (GET never consumes). */
export const InvitationPreview = z.object({
  status: lenient(z.enum(['valid', 'expired', 'revoked', 'accepted', 'already_member']), 'revoked'),
  companyName: z.string(),
  serverUrl: z.string(),
  email: z.string(),
  inviter: z.object({ displayName: z.string(), department: z.string() }).nullable(),
  expiresAt: Timestamp.nullable(),
});
export type InvitationPreview = z.infer<typeof InvitationPreview>;

/**
 * The one-time accept link, only from a server that sends no mail (ADR-0007): whoever issued the
 * invitation hands it over. Shown once; `resend` issues a new one and the old link stops working.
 */
const HandoffLink = z.url().optional();

const invitationParam = z.object({ invitationId: Id });
const invitationAction = (id: string, action: string) =>
  endpoint({
    id: `invitations.${id}`,
    method: 'POST',
    path: `/api/v1/invitations/:invitationId/${action}` as `/${string}`,
    auth: 'session',
    params: invitationParam,
    response: z.object({ invitation: Invitation, link: HandoffLink }),
    rateLimit: 'invite',
  });

export const invitationsEndpoints = {
  /** Admins (or members when direct invites are allowed) create PENDING invites; other members create REQUESTED ones. */
  create: endpoint({
    id: 'invitations.create',
    method: 'POST',
    path: '/api/v1/invitations',
    auth: 'session',
    body: z.object({ emails: z.array(z.string().max(254)).min(1).max(LIMITS.maxInviteBatch) }),
    response: z.object({
      results: z.array(z.object({ email: z.string(), outcome: lenient(InviteOutcome, 'invalid'), invitation: Invitation.nullable(), link: HandoffLink })),
    }),
    rateLimit: 'invite',
  }),
  /** Admins see every invitation; members see the ones they requested. */
  list: endpoint({
    id: 'invitations.list',
    method: 'GET',
    path: '/api/v1/invitations',
    auth: 'session',
    query: z.object({ status: InvitationStatus.optional() }),
    response: z.object({ invitations: z.array(Invitation) }),
  }),
  approve: invitationAction('approve', 'approve'),
  reject: invitationAction('reject', 'reject'),
  /** Issues a new token; the previous link stops working. */
  resend: invitationAction('resend', 'resend'),
  revoke: invitationAction('revoke', 'revoke'),
  /** Token from the URL fragment. Never consumes the invitation. */
  inspect: endpoint({
    id: 'invitations.inspect',
    method: 'POST',
    path: '/api/v1/invitations/inspect',
    auth: 'public',
    body: z.object({ token: z.string().min(16).max(256) }),
    response: InvitationPreview,
    rateLimit: 'auth',
  }),
  /** Atomically consumes the token and creates the instance-local account. */
  accept: endpoint({
    id: 'invitations.accept',
    method: 'POST',
    path: '/api/v1/invitations/accept',
    auth: 'public',
    body: z.object({
      token: z.string().min(16).max(256),
      displayName: singleLine({ min: 1, max: 40 }),
      password: NewPassword,
      acceptTerms: z.literal(true),
      client: ClientInfo,
    }),
    response: LoginOk,
    successStatus: 201,
    rateLimit: 'auth',
  }),
};
